import React, { useRef, useState } from "react";
import { Table, Button, Input, Alert } from "reactstrap";
import { fetchAttachmentDownload } from "./useContractReviewApi";

// Match the legacy 30 MB cap (Pass 1 §1.9).
const MAX_BYTES = 30 * 1024 * 1024;

/**
 * Attachments tab. Mirrors dgvAttach in legacy + the OpenFileDialog flow:
 *   • Pick a file (max 30 MB) and click Add. The file uploads immediately —
 *     unlike legacy which deferred to Save, web UX is better with eager upload.
 *   • Existing attachments: double-click → download (legacy writes to C:\db\temp
 *     and Process.Start; modern equivalent is a browser download).
 *   • Delete is gated server-side (submitter / submitter's supv / author);
 *     here we let everyone see the button and let the server reject if needed.
 */
export default function AttachmentsTab({
    attachments,
    onUpload,
    onDelete,
    isUploading,
    readOnly,
    canAttachAfterArchive,
    isArchived,
}) {
    const fileRef = useRef(null);
    const [error, setError] = useState("");

    const handleChange = async (e) => {
        setError("");
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_BYTES) {
            setError("File exceeds the 30 MB limit. Please choose a smaller file.");
            e.target.value = "";
            return;
        }
        try {
            await onUpload(f);
        } catch (err) {
            setError(err?.response?.data ?? err?.message ?? "Upload failed.");
        }
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleDownload = async (att) => {
        try {
            const { url, filename } = await fetchAttachmentDownload(att.id);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || att.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5_000);
        } catch (err) {
            setError(err?.response?.data ?? err?.message ?? "Download failed.");
        }
    };

    // When archived, only manager / admin may upload more (legacy btnMgrSave).
    const canUpload = !readOnly && (!isArchived || canAttachAfterArchive);

    return (
        <div>
            {error && <Alert color="danger" toggle={() => setError("")}>{error}</Alert>}

            <div className="d-flex gap-2 align-items-center mb-3">
                <Input
                    type="file"
                    bsSize="sm"
                    innerRef={fileRef}
                    onChange={handleChange}
                    disabled={!canUpload || isUploading}
                    style={{ maxWidth: 380 }}
                />
                {isUploading && <span className="text-muted">Uploading…</span>}
                {!canUpload && isArchived && !canAttachAfterArchive && (
                    <span className="text-muted small">CR is archived — only managers can add attachments.</span>
                )}
            </div>
            <p className="small text-muted">Maximum file size: 30 MB.</p>

            <Table size="sm" bordered responsive className="align-middle">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: "44%" }}>Filename</th>
                        <th style={{ width: "20%" }}>Submitted By</th>
                        <th style={{ width: "16%" }}>Date</th>
                        <th style={{ width: "20%" }} className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {(attachments || []).map((a) => (
                        <tr key={a.id} onDoubleClick={() => handleDownload(a)} style={{ cursor: "pointer" }}>
                            <td>
                                <button type="button" className="btn btn-link p-0 text-decoration-none" onClick={() => handleDownload(a)}>
                                    {a.filename}
                                </button>
                            </td>
                            <td>{a.submittedByName || a.submittedBy}</td>
                            <td>{formatDate(a.submittedOn)}</td>
                            <td className="text-end">
                                <Button size="sm" color="light" className="me-1" onClick={() => handleDownload(a)}>
                                    <i className="ri-download-line" />
                                </Button>
                                <Button
                                    size="sm"
                                    color="light"
                                    onClick={() => {
                                        if (window.confirm(`Delete attachment "${a.filename}"?`)) onDelete(a.id);
                                    }}
                                    disabled={readOnly}
                                >
                                    <i className="ri-delete-bin-line text-danger" />
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {(attachments || []).length === 0 && (
                        <tr>
                            <td colSpan={4} className="text-center text-muted py-3">No attachments yet.</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
}

function formatDate(s) {
    if (!s) return "";
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        return d.toLocaleDateString();
    } catch { return s; }
}
